/**
 * CSV 匯出工具函數
 * 支援將資料轉換為 CSV 格式並觸發瀏覽器下載
 */

/**
 * 轉義 CSV 欄位值
 * 處理包含逗號、引號、換行的值
 */
function escapeCSVValue(value: string): string {
  // 如果值包含逗號、引號或換行，需要用雙引號包圍
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    // 將雙引號轉義為兩個雙引號
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * 將資料轉換為 CSV 格式字串
 */
function convertToCSV<T>(
  data: T[],
  headers: { key: keyof T; label: string }[]
): string {
  // 建立標題行
  const headerRow = headers.map((h) => escapeCSVValue(h.label)).join(",");

  // 建立資料行
  const dataRows = data.map((row) => {
    return headers
      .map((header) => {
        const value = row[header.key];
        // 處理各種類型的值
        if (value === null || value === undefined) {
          return "";
        }
        if (typeof value === "string") {
          return escapeCSVValue(value);
        }
        if (typeof value === "number") {
          return value.toString();
        }
        if (value instanceof Date) {
          // 格式化日期為 YYYY-MM-DD HH:mm:ss
          const year = value.getFullYear();
          const month = String(value.getMonth() + 1).padStart(2, "0");
          const day = String(value.getDate()).padStart(2, "0");
          const hours = String(value.getHours()).padStart(2, "0");
          const minutes = String(value.getMinutes()).padStart(2, "0");
          const seconds = String(value.getSeconds()).padStart(2, "0");
          return escapeCSVValue(
            `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
          );
        }
        // 其他類型轉為字串
        return escapeCSVValue(String(value));
      })
      .join(",");
  });

  // 組合所有行
  return [headerRow, ...dataRows].join("\n");
}

/**
 * 匯出資料為 CSV 檔案
 * @param data 要匯出的資料陣列
 * @param headers 欄位定義（key: 資料欄位名稱, label: CSV 標題）
 * @param filename 檔案名稱（不含副檔名）
 */
export function exportToCSV<T>(
  data: T[],
  headers: { key: keyof T; label: string }[],
  filename: string
): void {
  if (data.length === 0) {
    console.warn("沒有資料可以匯出");
    return;
  }

  try {
    // 轉換為 CSV 格式
    const csvContent = convertToCSV(data, headers);

    // 添加 UTF-8 BOM 以確保 Excel 正確顯示中文
    const BOM = "\uFEFF";
    const csvWithBOM = BOM + csvContent;

    // 創建 Blob
    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });

    // 創建下載連結
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.csv`;

    // 觸發下載
    document.body.appendChild(link);
    link.click();

    // 清理
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("匯出 CSV 失敗:", error);
    throw new Error("匯出 CSV 時發生錯誤");
  }
}
